import React from 'react'
import styled from 'styled-components'
import { Box, get, TextBody } from '@ally/metronome-ui'
import { maskAccountNumber } from '@ally/aos-utils'
import {
  Account,
  AccountLOB,
  AccountStatus,
  AccountType,
} from '@ally/data-types-afg-accounts'
import { useHostServices } from '../../HostServices'
import {
  AccountTypeToLabel,
  getBalance,
  labelHeadings,
  isAccountBlocked,
  mortgageAccountAllytmlnText,
  getDashDash,
  isMortgagePaidOffHandled,
  LabelHeadingKeys,
  isRestrictedCCStatus,
} from '../../utils'
import WarningIcon from '../WarningIcon'
import {
  ModalTriggerProps,
  DELINQUENT_MORTGAGE_ARIA_LABEL,
} from '../../HomeLoans/Modals'
import { STALE_DATA_COPY } from '../../constants'
import {
  isAutoAccountPastDue,
  isAutoAccountWithNoActiveVehicle,
} from '../../Vehicles/utils'
import { TextBodyOverride } from '../MetronomeOverrides'
import { getStatus } from '../../CreditCard/TableColumns'

const StyledBoxWrapper = styled(Box)`
  white-space: normal;
`

const StyledButton = styled.button`
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  color: ${get.colors('bluesuedeshoes')};
  text-align: left;
`

const StyledValueHeading = styled(TextBody)`
  text-transform: capitalize;
`

interface LocaleOptions {
  month: '2-digit' | 'numeric' | 'short' | 'long' | 'narrow' | undefined
  day: '2-digit' | 'numeric' | undefined
  year: '2-digit' | 'numeric' | undefined
}

const ClosedAccount: React.FC<{ accountClosingDate: string }> = ({
  accountClosingDate,
}) => {
  const options: LocaleOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
  return (
    <TextBody tag="p" size="sm" type="error">
      {new Date(accountClosingDate).toLocaleDateString('en-US', options)}
    </TextBody>
  )
}

interface GetAccountValueProps {
  accountClosingDate?: string
  FormattedCurrency: React.ReactElement
  dashDashAssistiveText?: string
}

export const getAccountValue = ({
  accountClosingDate,
  FormattedCurrency,
  dashDashAssistiveText,
}: GetAccountValueProps): React.ReactNode => {
  if (accountClosingDate) {
    return <ClosedAccount accountClosingDate={accountClosingDate} />
  }
  if (dashDashAssistiveText) {
    return getDashDash(dashDashAssistiveText)
  }

  return FormattedCurrency
}

const FormattedCurrency: React.FC<{
  balance: number
  pastDue: boolean
  isCreditCard?: boolean
}> = ({ balance, pastDue, isCreditCard }) => (
  <TextBodyOverride
    tag="p"
    size="md"
    color={pastDue || (balance < 0 && !isCreditCard) ? 'error' : 'slate-5'}
  >
    {Intl.NumberFormat('en-us', {
      style: 'currency',
      currency: 'USD',
    }).format(balance)}
  </TextBodyOverride>
)

type GetMobileCardValueHeadingProps = {
  type: AccountType
  status: AccountStatus
  isCreditCardEnabled?: boolean
}

export const getMobileCardValueHeading = ({
  type,
  status,
  isCreditCardEnabled,
}: GetMobileCardValueHeadingProps): React.ReactNode => {
  const accountTypeLabel = AccountTypeToLabel[type] as LabelHeadingKeys
  const valueHeading = labelHeadings[accountTypeLabel]

  if (
    isCreditCardEnabled &&
    accountTypeLabel === 'CC' &&
    status === AccountStatus.CLOSED &&
    valueHeading
  ) {
    return (
      <StyledValueHeading tag="p" size="md" weight="bold">
        {valueHeading}
      </StyledValueHeading>
    )
  }

  if (status === AccountStatus.CLOSED) {
    return (
      <TextBody tag="p" size="md" type="error">
        CLOSED
      </TextBody>
    )
  }

  if (valueHeading) {
    return (
      <StyledValueHeading tag="p" size="md" weight="bold">
        {valueHeading}
      </StyledValueHeading>
    )
  }

  return null
}

type MobileCardDetailProps = {
  account: Account
  hasStaleData?: boolean
  openMortgageModal?: () => void
  focusRef?: React.RefObject<HTMLButtonElement>
} & ModalTriggerProps

const MobileCardDetail = ({
  account,
  hasStaleData,
  openModal: openSpecialScenarioModal,
  openMortgageModal = undefined,
  focusRef,
}: MobileCardDetailProps): JSX.Element => {
  const { featureFlags } = useHostServices()

  const isCreditCardEnabled = featureFlags.variation(
    'FF_credit-card-snapshot',
    false,
  )

  const { nickName, accountNumber, type, status, name } = account
  // istanbul ignore next
  const accNickname = nickName || name
  const isDepositAccount = account.lob === AccountLOB.DEPOSIT
  const isMortgageAccount = account.lob === AccountLOB.MORTGAGE
  const isCCAccount = account.lob === AccountLOB.CC
  const isInvestAccount = account.lob === AccountLOB.INVEST
  const isAutoAccount = account.lob === AccountLOB.AUTO

  const paymentDue = account.meta?.nextPayment?.totalAmountDue

  const handleAvailableBalance = (): number => {
    if (isMortgageAccount) {
      if (isMortgagePaidOffHandled(account)) {
        return 0
      }
      // istanbul ignore next
      return account.meta?.amountDue || 0
    }
    // istanbul ignore next
    return account.balance?.available || 0
  }

  const handleCurrentBalance = (): number => {
    if (isMortgageAccount) {
      if (isMortgagePaidOffHandled(account)) {
        return 0
      }
      // istanbul ignore next
      return account.meta?.amountDue || 0
    }
    if (isCCAccount) {
      return account.balance?.current || 0
    }
    if (isInvestAccount) {
      return account.value?.total ?? 0
    }
    // isDepositAccount
    // istanbul ignore next
    return account.balance?.current || 0
  }

  const availableBalance = handleAvailableBalance()
  const currentBalance = handleCurrentBalance()
  const showBalance =
    isMortgageAccount ||
    isCCAccount ||
    isDepositAccount ||
    isAutoAccount ||
    (isInvestAccount && status !== AccountStatus.CLOSED)

  const accountIsBlocked = isAccountBlocked(account)

  const hasCurrentBalance = typeof account.balance?.current === 'number'
  const isClosedWithValue =
    account.status === AccountStatus.CLOSED && hasCurrentBalance
  const hasRestrictedCCStatus =
    isCCAccount && isRestrictedCCStatus(account.status) && !isClosedWithValue

  const modalAriaLabel = account.accountNumber
    ? `${accNickname} account ending in ${maskAccountNumber(
        account.accountNumber,
        'none',
      )}`
    : ''

  const getAssistiveText = (): string | undefined => {
    if (hasStaleData) {
      return STALE_DATA_COPY
    }
    if (accountIsBlocked) {
      const accountTypeLabel = AccountTypeToLabel[type] as LabelHeadingKeys
      const valueHeading = labelHeadings[accountTypeLabel]
      return `${valueHeading} not available. Call for information.`
    }
    if (hasRestrictedCCStatus) {
      return getStatus(account) || '--'
    }

    return undefined
  }

  function mortgageModalButtonAriaLabel(): string | undefined {
    if (account.indicator?.blocked) {
      return DELINQUENT_MORTGAGE_ARIA_LABEL.concat(` ${modalAriaLabel}`)
    }
    return undefined
  }

  const isMortgageCard = !!openMortgageModal

  /**
   * Returns the proper onClick function for opening a modal or undefined
   * @returns function | undefined
   */
  function handleAccountClick(): (() => void) | undefined {
    /* if openDelinquentModal exists use that function otherwise the regular
       mortgage modal trigger function */
    if (isMortgageCard) {
      return openSpecialScenarioModal ?? openMortgageModal
    }
    return undefined
  }

  const nicknameSpan = (
    <TextBodyOverride
      tag="span"
      color={hasRestrictedCCStatus ? 'slate-5' : 'bluesuedeshoes'}
      size="lg"
      weight="bold"
    >
      {accNickname}
    </TextBodyOverride>
  )

  return (
    <StyledBoxWrapper
      onClick={handleAccountClick()}
      data-allytmln={
        isMortgageCard ? mortgageAccountAllytmlnText(account) : undefined
      }
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderLeftStyle="solid"
        borderLeftWidth="7px"
        p="xs"
        borderTop="1px solid"
        borderTopColor="slate-2"
        width="100%"
      >
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          alignItems="flex-start"
          textAlign="left"
        >
          <Box display="flex" flexDirection="column">
            <div>
              <Box display="flex" alignItems="center">
                {accountIsBlocked && <WarningIcon style={{ flexShrink: 0 }} />}

                {isMortgageCard ? (
                  // onClick is on the wrapper component
                  <StyledButton
                    ref={focusRef}
                    data-testid="snapshot-mobile-account-button"
                    aria-label={mortgageModalButtonAriaLabel()}
                  >
                    {nicknameSpan}
                  </StyledButton>
                ) : (
                  nicknameSpan
                )}
              </Box>
            </div>
            <TextBody tag="span" size="sm">
              {maskAccountNumber(accountNumber)}
            </TextBody>
          </Box>
        </Box>
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          alignItems="flex-end"
        >
          <Box>
            <TextBody tag="p" size="xs" weight="bold">
              {getMobileCardValueHeading({
                type,
                status,
                isCreditCardEnabled,
              })}
            </TextBody>
          </Box>
          {showBalance && (
            <Box>
              {!isAutoAccountWithNoActiveVehicle(account) ? (
                getAccountValue({
                  accountClosingDate: account.closeDate,
                  FormattedCurrency: (
                    <FormattedCurrency
                      balance={getBalance({
                        accountType: type,
                        availableBalance,
                        currentBalance,
                        paymentDue,
                      })}
                      isCreditCard={isCCAccount}
                      pastDue={isAutoAccountPastDue(account)}
                    />
                  ),
                  dashDashAssistiveText: getAssistiveText(),
                })
              ) : (
                <TextBody tag="p" size="md">
                  Unavailable
                </TextBody>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </StyledBoxWrapper>
  )
}

export default MobileCardDetail
